import type { KeyboardEvent as ReactKeyboardEvent } from "react";

/**
 * Commit a single-line input on Enter by blurring it. Issues #551 / #552.
 *
 * Blurring IS the commit for these fields — they all write through `onChange`,
 * so the value is already in state; the only thing Enter needs to do is get the
 * keyboard out of the way and make the field feel finished.
 *
 * ⚠️ Not enough on its own. A correct onKeyDown handler still dies to iOS
 * autocorrect: when the autocorrect bar has a live suggestion, Return ACCEPTS
 * the suggestion instead of firing a clean Enter keydown, so this never runs.
 * Any field wired to this also needs `autoCorrect="off"` and `spellCheck={false}`
 * — unless it takes real prose, where losing autocorrect costs more than the
 * Enter fix gains. See the CLAUDE.md foot-gun "A correct onKeyDown Enter handler
 * still dies to iOS autocorrect". It does not reproduce in a desktop browser.
 *
 * Also pair it with `enterKeyHint` so the iOS key is labelled "Done"/"Go"/
 * "Search" rather than the default "return" — half of why a working handler
 * still felt dead.
 *
 * ⚠️ NOT for textareas. Enter is a newline there.
 *
 * Extracted here in #552: byte-identical copies had already accumulated in
 * BriefingBuilder.tsx and TasksTab.tsx, and hand-synced copies of one rule is
 * this codebase's most common defect shape.
 */
export const blurOnEnter = (e: ReactKeyboardEvent<HTMLInputElement>) => {
  if (e.key !== "Enter") return;
  e.preventDefault();
  e.currentTarget.blur();
};
